import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { EnquiryContext, useCurrentPath } from './EnquiryContext';
import { LazyDialog, lazyDialog } from './lazyDialog';

// The form (fields, validation, categories, POST /api/enquiries) and the Radix dialog load
// on demand: their own chunk, fetched on the first open or when a trigger is hovered or
// focused (preloadEnquiryDialog).
const enquiryDialog = lazyDialog(() => import(/* webpackChunkName: "enquiry-dialog" */ './EnquiryDialog'));

/** Start loading the enquiry dialog now (hover / focus of a trigger). Safe to call often. */
export const preloadEnquiryDialog = () => enquiryDialog.preload();

const ENQUIRY_LOAD_ERROR = "The enquiry form couldn't open just now. Check your connection and try again, or email hello@tappd.app.";

const normaliseRole = (role) => (role === 'venue' ? 'venue' : 'advertiser');

/**
 * One enquiry dialog and the calls that open it (shared by EnquiryProvider and the
 * provider-less fallback of useEnquiryDialog). → { open(options), close(), isOpen, dialog }
 * where `dialog` is the element to render once.
 *
 * open({ role = 'advertiser', planContext, sourcePath }):
 * - `role`: 'advertiser' | 'venue' (anything else is 'advertiser')
 * - `planContext`: { tier, interval, venue_count, venue_ids } — what the visitor was looking
 *   at (shown in the dialog, removable, sent as plan_context); replaced on every open
 * - `sourcePath`: the page path sent with the enquiry (default: the router's current
 *   pathname, else window.location.pathname)
 * Focus returns to the element that had it when open() was called. The dialog body is
 * loaded on the first open (a small "Loading…" panel shows until it arrives, unless a
 * trigger preloaded it) and then stays mounted, so a half-typed enquiry survives closing.
 */
export function useEnquiryController() {
  const currentPath = useCurrentPath();
  const pathRef = useRef(currentPath);
  pathRef.current = currentPath;
  const returnFocusRef = useRef(null);
  const [session, setSession] = useState({ open: false, key: 0, role: 'advertiser', planContext: null, sourcePath: null });

  const open = useCallback((options = {}) => {
    const opts = options && typeof options === 'object' ? options : {};
    if (typeof document !== 'undefined') returnFocusRef.current = document.activeElement;
    const fallbackPath = typeof window !== 'undefined' ? window.location.pathname : null;
    setSession((s) => ({
      open: true,
      key: s.key + 1,
      role: normaliseRole(opts.role),
      planContext: opts.planContext || null,
      sourcePath: opts.sourcePath !== undefined && opts.sourcePath !== null ? opts.sourcePath : (pathRef.current || fallbackPath),
    }));
  }, []);

  const setOpen = useCallback((next) => setSession((s) => (s.open === next ? s : { ...s, open: next })), []);
  const close = useCallback(() => setOpen(false), [setOpen]);
  const clearPlanContext = useCallback(() => setSession((s) => ({ ...s, planContext: null })), []);

  // Closing from the loading / load-error panel (the dialog itself returns focus on close).
  const closePending = useCallback(() => {
    setOpen(false);
    const el = returnFocusRef.current;
    if (el && el.isConnected && typeof el.focus === 'function') el.focus();
  }, [setOpen]);

  const dialog = (
    <LazyDialog
      dialog={enquiryDialog}
      open={session.open}
      onClose={closePending}
      errorMessage={ENQUIRY_LOAD_ERROR}
      render={(EnquiryDialog) => (
        <EnquiryDialog
          session={session}
          onOpenChange={setOpen}
          onClearPlanContext={clearPlanContext}
          returnFocusRef={returnFocusRef}
        />
      )}
    />
  );
  return { open, close, isOpen: session.open, dialog };
}

/**
 * The page-wide enquiry dialog: renders `children` and one EnquiryDialog, and gives
 * useEnquiry() its `{ open(options), close() }`. PublicLayout mounts it on the redesigned
 * site (SLOT_MODEL on), so every page can open the same dialog and a half-typed enquiry
 * survives closing it. Nothing is rendered for the dialog until it opens.
 */
export default function EnquiryProvider({ children }) {
  const controller = useEnquiryController();
  const { open, close } = controller;
  const value = useMemo(() => ({ open, close, available: true }), [open, close]);
  return (
    <EnquiryContext.Provider value={value}>
      {children}
      {controller.dialog}
    </EnquiryContext.Provider>
  );
}

/**
 * Open the enquiry dialog with or without an EnquiryProvider above: with one it uses the
 * page-wide dialog (`dialog` is null); without one it keeps its own dialog, which the
 * caller must render: `const { open, dialog } = useEnquiryDialog(); … {dialog}`.
 * → { open(options), close(), dialog }
 */
export function useEnquiryDialog() {
  const ctx = useContext(EnquiryContext);
  const local = useEnquiryController();
  if (ctx) return { open: ctx.open, close: ctx.close, dialog: null };
  return { open: local.open, close: local.close, dialog: local.dialog };
}
